import { useState, useMemo, useRef, useEffect } from "react";
import type { ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TableHead } from "@/components/ui/table";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, SlidersHorizontal, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export type SortDir = "asc" | "desc" | null;

export interface NumericFilter {
  min?: number;
  max?: number;
}

export interface ColumnSort<F extends string = string> {
  field: F;
  dir: SortDir;
  numFilter?: NumericFilter;
  textFilter?: Set<string>;
}

/* ─── Sortable Numeric Column Header ─── */
export function NumericColumnHeader<F extends string>({
  label,
  field,
  sort,
  onSort,
  align,
  tooltip,
}: {
  label: string;
  field: F;
  sort: ColumnSort<F> | null;
  onSort: (s: ColumnSort<F> | null) => void;
  align?: "left" | "center" | "right";
  tooltip?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [minVal, setMinVal] = useState("");
  const [maxVal, setMaxVal] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isActive =
    sort?.field === field &&
    (sort.dir || sort.numFilter?.min != null || sort.numFilter?.max != null);
  const currentDir = sort?.field === field ? sort.dir : null;
  const hasRange =
    sort?.field === field && (sort.numFilter?.min != null || sort.numFilter?.max != null);

  const cycleSort = () => {
    if (currentDir === null)
      onSort({ field, dir: "desc", numFilter: sort?.field === field ? sort.numFilter : undefined });
    else if (currentDir === "desc")
      onSort({ field, dir: "asc", numFilter: sort?.field === field ? sort.numFilter : undefined });
    else onSort(hasRange ? { field, dir: null, numFilter: sort?.numFilter } : null);
  };

  const applyMinMax = () => {
    const min = minVal ? parseFloat(minVal) : undefined;
    const max = maxVal ? parseFloat(maxVal) : undefined;
    if (min == null && max == null && !currentDir) {
      onSort(null);
    } else {
      onSort({ field, dir: currentDir || "desc", numFilter: { min, max } });
    }
    setOpen(false);
  };

  const clearAll = () => {
    setMinVal("");
    setMaxVal("");
    onSort(null);
    setOpen(false);
  };

  const alignClass =
    align === "left" ? "text-left" : align === "center" ? "text-center" : "text-right";

  return (
    <TableHead className={cn("text-xs font-semibold min-w-[80px]", alignClass)}>
      <div ref={ref} className="relative inline-flex items-center gap-0">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={cycleSort}
                className={cn(
                  "inline-flex items-center gap-1 px-1.5 py-1 rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                <span className="leading-tight">{label}</span>
                {currentDir === "desc" ? (
                  <ArrowDown className="w-3 h-3 shrink-0" />
                ) : currentDir === "asc" ? (
                  <ArrowUp className="w-3 h-3 shrink-0" />
                ) : (
                  <ArrowUpDown className="w-3 h-3 opacity-40 shrink-0" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px]">
              Click to sort{" "}
              {currentDir === null ? "descending" : currentDir === "desc" ? "ascending" : "default"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center p-1 cursor-help">
                  <Info className="w-3 h-3 text-muted-foreground/60 shrink-0" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[240px] text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setOpen(!open)}
                className={cn(
                  "p-1 rounded-md transition-colors hover:bg-accent",
                  hasRange ? "text-primary" : "text-muted-foreground/50"
                )}
              >
                <SlidersHorizontal className="w-3 h-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px]">
              Set min/max range
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {open && (
          <div className="absolute z-50 top-full right-0 mt-1 w-52 rounded-lg border bg-popover shadow-lg p-3 space-y-3">
            <p className="text-[11px] font-semibold text-foreground">Filter Range</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-[10px] text-muted-foreground font-medium">Min</label>
                <Input
                  placeholder="0"
                  value={minVal}
                  onChange={(e) => setMinVal(e.target.value)}
                  className="h-8 text-xs"
                  type="number"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-muted-foreground font-medium">Max</label>
                <Input
                  placeholder="∞"
                  value={maxVal}
                  onChange={(e) => setMaxVal(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-xs flex-1" onClick={applyMinMax}>
                Apply
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={clearAll}>
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>
    </TableHead>
  );
}

/* ─── Sortable Text Column Header ─── */
export function TextColumnHeader<F extends string>({
  label,
  field,
  sort,
  onSort,
  allValues,
  align,
}: {
  label: string;
  field: F;
  sort: ColumnSort<F> | null;
  onSort: (s: ColumnSort<F> | null) => void;
  allValues: string[];
  align?: "left" | "center" | "right";
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isActive =
    sort?.field === field && (sort.dir || (sort.textFilter && sort.textFilter.size > 0));
  const currentDir = sort?.field === field ? sort.dir : null;
  const currentTextFilter = sort?.field === field ? sort.textFilter : undefined;

  const cycleSort = () => {
    const keepFilter =
      currentTextFilter && currentTextFilter.size > 0 ? currentTextFilter : undefined;
    if (currentDir === null) onSort({ field, dir: "asc", textFilter: keepFilter });
    else if (currentDir === "asc") onSort({ field, dir: "desc", textFilter: keepFilter });
    else onSort(keepFilter ? { field, dir: null, textFilter: keepFilter } : null);
  };

  const unique = useMemo(() => [...new Set(allValues)].sort(), [allValues]);
  const filtered = query
    ? unique.filter((v) => v.toLowerCase().includes(query.toLowerCase()))
    : unique;

  const toggleVal = (v: string) => {
    const next = new Set(currentTextFilter || []);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    if (next.size === 0 && !currentDir) {
      onSort(null);
    } else {
      onSort({ field, dir: currentDir, textFilter: next.size > 0 ? next : undefined });
    }
  };

  const clearAll = () => {
    onSort(null);
    setOpen(false);
  };

  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  return (
    <TableHead className={cn("text-xs font-semibold min-w-[80px]", alignClass)}>
      <div ref={ref} className="relative inline-flex items-center gap-0">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={cycleSort}
                className={cn(
                  "inline-flex items-center gap-1 px-1.5 py-1 rounded-md transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                <span>{label}</span>
                {currentDir === "asc" ? (
                  <ArrowUp className="w-3 h-3" />
                ) : currentDir === "desc" ? (
                  <ArrowDown className="w-3 h-3" />
                ) : (
                  <ArrowUpDown className="w-3 h-3 opacity-40" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px]">
              Click to sort{" "}
              {currentDir === null ? "A → Z" : currentDir === "asc" ? "Z → A" : "default"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setOpen(!open)}
                className={cn(
                  "p-1 rounded-md transition-colors hover:bg-accent relative",
                  currentTextFilter && currentTextFilter.size > 0
                    ? "text-primary"
                    : "text-muted-foreground/50"
                )}
              >
                <Search className="w-3 h-3" />
                {currentTextFilter && currentTextFilter.size > 0 && (
                  <span className="absolute -top-1 -right-1 text-[8px] bg-primary text-primary-foreground rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none">
                    {currentTextFilter.size}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px]">
              Filter by value
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {open && (
          <div className="absolute z-50 top-full left-0 mt-1 w-56 rounded-lg border bg-popover shadow-lg">
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                <input
                  autoFocus
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 text-xs rounded border-none bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2 text-center">No results</p>
              ) : (
                filtered.map((v) => (
                  <label
                    key={v}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={currentTextFilter?.has(v) || false}
                      onCheckedChange={() => toggleVal(v)}
                      className="h-3 w-3"
                    />
                    <span className="text-xs truncate">{v}</span>
                  </label>
                ))
              )}
            </div>
            {currentTextFilter && currentTextFilter.size > 0 && (
              <div className="p-1.5 border-t">
                <button onClick={clearAll} className="text-[10px] text-primary hover:underline">
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </TableHead>
  );
}
